import { useState, useCallback, useEffect, useMemo } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  UsersRound,
  ArrowLeft,
  Users,
  Plus,
  Loader2,
  Trash2,
  CheckCircle,
  XCircle,
  MoreHorizontal,
  Pencil,
  ShieldCheck,
  ArrowUpRight,
  ClipboardCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ObjectPermissionActionsMenu } from "@/components/portal/platform/ObjectPermissionActionsMenu";
import { objectPermissionLink } from "@/components/portal/groups/objectPermissionLinks";
import { ObjectGroupAccessDialog } from "@/components/portal/groups/ObjectGroupAccessDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Pagination } from "@/components/ui/pagination";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { platformApi, OrganisationMember } from "@/services/api/platform";
import {
  groupsApi,
  Group,
  GroupMember,
  ObjectPermission,
} from "@/services/api/groups";
import { usePaginatedData, PaginatedResponse } from "@/hooks/usePaginatedData";

export const Route = createFileRoute("/portal/platform/groups_/$groupId")({
  component: GroupDetailPage,
});

interface EditFormData {
  name: string;
  description: string;
}

/**
 * Sends the user to the page where this grant is actually managed. Grants are created
 * on the object's own page, never here — see the card description.
 */
function ManageAtObjectLink({ perm }: { perm: ObjectPermission }): JSX.Element | null {
  const link = objectPermissionLink(perm);
  if (!link) return null;

  const label = `Manage access for ${perm.object_name || perm.object_id} on its own page`;

  return (
    <Button asChild variant="ghost" size="icon" className="h-8 w-8">
      {/*
        `to` is resolved at runtime from the grant row, so it cannot be checked against
        the generated route union here. Every value comes from the fixed table in
        objectPermissionLinks.ts, which is covered by its own unit test.
      */}
      <Link
        {...(link as unknown as { to: string })}
        title="Manage at object"
        aria-label={label}
      >
        <ArrowUpRight className="h-4 w-4" />
      </Link>
    </Button>
  );
}

function GroupDetailPage() {
  const { groupId } = Route.useParams();
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { can, isAdmin } = usePermissions();
  const navigate = useNavigate();

  const organisationId = selectedOrganisation?.organisationId;

  // Group data
  const [group, setGroup] = useState<Group | null>(null);
  const [loadingGroup, setLoadingGroup] = useState(true);
  const [groupError, setGroupError] = useState<string | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState("members");

  // Members state
  const [memberSearchTerm, setMemberSearchTerm] = useState("");
  const [memberSortBy, setMemberSortBy] = useState("email");
  const [memberSortOrder, setMemberSortOrder] = useState<"asc" | "desc">("asc");
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [removingMember, setRemovingMember] = useState<GroupMember | null>(
    null,
  );
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(
    new Set(),
  );
  const [addingMember, setAddingMember] = useState(false);
  const [removingMemberSubmitting, setRemovingMemberSubmitting] =
    useState(false);
  // Add member dialog - server-side search & pagination
  const [addMemberSearchTerm, setAddMemberSearchTerm] = useState("");

  // Object Permissions state
  const [_objPermSearchTerm, _setObjPermSearchTerm] = useState("");
  const [objPermSortBy, setObjPermSortBy] = useState("created_at");
  const [objPermSortOrder, setObjPermSortOrder] = useState<"asc" | "desc">("desc");
  const [objPermServiceFilter, setObjPermServiceFilter] = useState<string>("");
  const [objPermObjectTypeFilter, setObjPermObjectTypeFilter] = useState<string>("");
  const [removingObjPerm, setRemovingObjPerm] = useState<ObjectPermission | null>(null);
  const [removingObjPermSubmitting, setRemovingObjPermSubmitting] = useState(false);

  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showReviewersDialog, setShowReviewersDialog] = useState(false);
  const [editFormData, setEditFormData] = useState<EditFormData>({
    name: "",
    description: "",
  });
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Load group data
  useEffect(() => {
    async function loadGroup() {
      if (!token || !organisationId || !groupId) return;

      setLoadingGroup(true);
      setGroupError(null);
      try {
        const data = await groupsApi.getGroup(token, organisationId, groupId);
        setGroup(data);
        setEditFormData({
          name: data.name,
          description: data.description || "",
        });
      } catch (err) {
        console.error("Failed to load group:", err);
        setGroupError(err instanceof Error ? err.message : "Failed to load group");
      } finally {
        setLoadingGroup(false);
      }
    }
    loadGroup();
  }, [token, organisationId, groupId]);

  // =============================================
  // Members
  // =============================================

  const fetchMembers = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<GroupMember>> => {
      if (!token || !organisationId || !groupId) {
        return { data: [], totalCount: 0, page, pageSize };
      }
      const response = await groupsApi.listGroupMembers(
        token,
        organisationId,
        groupId,
        {
          offset: (page - 1) * pageSize,
          limit: pageSize,
          search: memberSearchTerm || undefined,
          sort_by: memberSortBy,
          sort_order: memberSortOrder,
        },
      );
      return {
        data: response.items || [],
        totalCount: response.total || 0,
        page,
        pageSize,
      };
    },
    [token, organisationId, groupId, memberSearchTerm, memberSortBy, memberSortOrder],
  );

  const {
    data: members,
    isLoading: membersLoading,
    pagination: membersPagination,
    pageSizeOptions: membersPageSizeOptions,
    goToPage: membersGoToPage,
    nextPage: membersNextPage,
    prevPage: membersPrevPage,
    firstPage: membersFirstPage,
    lastPage: membersLastPage,
    setPageSize: membersSetPageSize,
    refresh: refreshMembers,
    canGoNext: membersCanGoNext,
    canGoPrev: membersCanGoPrev,
    startIndex: membersStartIndex,
    endIndex: membersEndIndex,
  } = usePaginatedData<GroupMember>({
    fetchFn: fetchMembers,
    initialPageSize: 25,
    dependencies: [
      token,
      organisationId,
      groupId,
      memberSearchTerm,
      memberSortBy,
      memberSortOrder,
    ],
  });

  const handleMemberSort = (column: string) => {
    if (memberSortBy === column) {
      setMemberSortOrder(memberSortOrder === "asc" ? "desc" : "asc");
    } else {
      setMemberSortBy(column);
      setMemberSortOrder("asc");
    }
  };

  const getMemberSortDirection = (column: string): "asc" | "desc" | null => {
    return memberSortBy === column ? memberSortOrder : null;
  };

  // Fetch available users for add member dialog - server-side pagination
  const fetchAvailableUsers = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<OrganisationMember>> => {
      if (!token || !organisationId || !showAddMemberDialog) {
        return { data: [], totalCount: 0, page, pageSize };
      }
      try {
        const response = await platformApi.listOrganisationMembers(
          token,
          organisationId,
          {
            offset: (page - 1) * pageSize,
            limit: pageSize,
            search: addMemberSearchTerm || undefined,
            sort_by: "first_name",
            sort_order: "asc",
          },
        );
        // Filter out users already in the group
        const memberUserIds = new Set(members.map((m) => m.user_id));
        const filtered = response.items.filter((u) => !memberUserIds.has(u.id));
        return {
          data: filtered,
          totalCount: response.total - (response.items.length - filtered.length),
          page,
          pageSize,
        };
      } catch (err) {
        console.error("Failed to load users:", err);
        toast.error("Failed to load available users");
        return { data: [], totalCount: 0, page, pageSize };
      }
    },
    [token, organisationId, addMemberSearchTerm, showAddMemberDialog, members],
  );

  const {
    data: availableUsers,
    isLoading: availableUsersLoading,
    pagination: availableUsersPagination,
    goToPage: availableUsersGoToPage,
    nextPage: availableUsersNextPage,
    prevPage: availableUsersPrevPage,
    firstPage: availableUsersFirstPage,
    lastPage: availableUsersLastPage,
    setPageSize: availableUsersSetPageSize,
    canGoNext: availableUsersCanGoNext,
    canGoPrev: availableUsersCanGoPrev,
    startIndex: availableUsersStartIndex,
    endIndex: availableUsersEndIndex,
  } = usePaginatedData<OrganisationMember>({
    fetchFn: fetchAvailableUsers,
    initialPageSize: 10,
    dependencies: [token, organisationId, addMemberSearchTerm, showAddMemberDialog, members],
  });

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleAddMembers = async () => {
    if (!token || !organisationId || !groupId || selectedUserIds.size === 0) return;

    setAddingMember(true);
    try {
      // One request for the whole selection. Fanning out per user meant a
      // 30-person add was 30 round trips that could half-succeed, leaving no way
      // to say which ones landed.
      await groupsApi.addGroupMembers(
        token,
        organisationId,
        groupId,
        Array.from(selectedUserIds),
      );
      toast.success(
        `${selectedUserIds.size} member(s) added to group`,
      );
      setShowAddMemberDialog(false);
      setSelectedUserIds(new Set());
      setAddMemberSearchTerm("");
      refreshMembers();
      // Reload group to update member count
      const data = await groupsApi.getGroup(token, organisationId, groupId);
      setGroup(data);
    } catch (err) {
      console.error("Failed to add members:", err);
      toast.error("Failed to add members to group");
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!token || !organisationId || !groupId || !removingMember) return;

    setRemovingMemberSubmitting(true);
    try {
      await groupsApi.removeGroupMember(
        token,
        organisationId,
        groupId,
        removingMember.user_id,
      );
      toast.success("Member removed from group");
      setRemovingMember(null);
      refreshMembers();
      // Reload group to update member count
      const data = await groupsApi.getGroup(token, organisationId, groupId);
      setGroup(data);
    } catch (err) {
      console.error("Failed to remove member:", err);
      toast.error("Failed to remove member from group");
    } finally {
      setRemovingMemberSubmitting(false);
    }
  };

  // =============================================
  // Object Permissions
  // =============================================

  const fetchObjectPermissions = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<ObjectPermission>> => {
      if (!token || !organisationId || !groupId) {
        return { data: [], totalCount: 0, page, pageSize };
      }
      const response = await groupsApi.listGroupObjectPermissions(
        token,
        organisationId,
        groupId,
        {
          offset: (page - 1) * pageSize,
          limit: pageSize,
          service_name: objPermServiceFilter || undefined,
          object_type: objPermObjectTypeFilter || undefined,
        },
      );
      return {
        data: response.items || [],
        totalCount: response.total || 0,
        page,
        pageSize,
      };
    },
    [token, organisationId, groupId, objPermServiceFilter, objPermObjectTypeFilter],
  );

  const {
    data: objectPermissions,
    isLoading: objPermsLoading,
    pagination: objPermsPagination,
    pageSizeOptions: objPermsPageSizeOptions,
    goToPage: objPermsGoToPage,
    nextPage: objPermsNextPage,
    prevPage: objPermsPrevPage,
    firstPage: objPermsFirstPage,
    lastPage: objPermsLastPage,
    setPageSize: objPermsSetPageSize,
    canGoNext: objPermsCanGoNext,
    canGoPrev: objPermsCanGoPrev,
    startIndex: objPermsStartIndex,
    endIndex: objPermsEndIndex,
    refresh: refreshObjPerms,
  } = usePaginatedData<ObjectPermission>({
    fetchFn: fetchObjectPermissions,
    initialPageSize: 25,
    dependencies: [
      token,
      organisationId,
      groupId,
      objPermServiceFilter,
      objPermObjectTypeFilter,
    ],
  });

  // Service display names for object permissions
  const serviceDisplayNames: Record<string, string> = useMemo(() => ({
    rag: "KB File",
    sql_rag: "KB SQL",
    reporting: "Reporting",
    myapp: "MyApp",
    connectors: "Connector",
    spaces: "Spaces",
    lms: "LMS",
  }), []);

  const [objectTypesByService, setObjectTypesByService] = useState<Record<string, string[]>>({});
  const [rolesByService, setRolesByService] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (!token || !organisationId) return;

    const services = ["rag", "sql_rag", "reporting", "myapp", "connectors", "spaces", "lms"];
    Promise.all(
      services.map(async (svc) => {
        try {
          const config = await groupsApi.getObjectPermissionConfig(token, organisationId, svc);
          return [
            svc,
            config.object_types.map((t) => t.object_type),
            config.roles.map((r) => r.code),
          ] as const;
        } catch {
          return [svc, [] as string[], [] as string[]] as const;
        }
      }),
    ).then((results) => {
      const types: Record<string, string[]> = {};
      const roles: Record<string, string[]> = {};
      for (const [svc, objTypes, roleCodes] of results) {
        if (objTypes.length > 0) types[svc] = objTypes;
        if (roleCodes.length > 0) roles[svc] = roleCodes;
      }
      setObjectTypesByService(types);
      setRolesByService(roles);
    });
  }, [token, organisationId]);

  const [changingRolePermId, setChangingRolePermId] = useState<string | null>(null);

  const handleChangeObjPermRole = useCallback(
    async (perm: ObjectPermission, newRole: string) => {
      if (!token || !organisationId || !groupId) return;
      if (perm.role === newRole) return;

      setChangingRolePermId(perm.id);
      try {
        await groupsApi.updateObjectPermission(
          token,
          organisationId,
          groupId,
          perm.id,
          { role: newRole },
        );
        toast.success(`Role changed to ${newRole}`);
        refreshObjPerms();
      } catch (err) {
        console.error("Failed to change object permission role:", err);
        toast.error("Failed to change role");
      } finally {
        setChangingRolePermId(null);
      }
    },
    [token, organisationId, groupId, refreshObjPerms],
  );

  // Object Permissions handlers
  const _handleObjPermSort = (column: string) => {
    if (objPermSortBy === column) {
      setObjPermSortOrder(objPermSortOrder === "asc" ? "desc" : "asc");
    } else {
      setObjPermSortBy(column);
      setObjPermSortOrder("asc");
    }
  };

  const _getObjPermSortDirection = (column: string): "asc" | "desc" | null => {
    return objPermSortBy === column ? objPermSortOrder : null;
  };

  const handleRemoveObjPerm = async () => {
    if (!token || !organisationId || !groupId || !removingObjPerm) return;

    setRemovingObjPermSubmitting(true);
    try {
      await groupsApi.removeObjectPermissions(token, organisationId, groupId, {
        permission_ids: [removingObjPerm.id],
      });
      toast.success("Object permission removed");
      setRemovingObjPerm(null);
      refreshObjPerms();
    } catch (err) {
      console.error("Failed to remove object permission:", err);
      toast.error("Failed to remove object permission");
    } finally {
      setRemovingObjPermSubmitting(false);
    }
  };

  // =============================================
  // Edit Group
  // =============================================

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !organisationId || !groupId) return;

    setEditSubmitting(true);
    try {
      const updated = await groupsApi.updateGroup(
        token,
        organisationId,
        groupId,
        {
          name: editFormData.name,
          description: editFormData.description || undefined,
        },
      );
      setGroup(updated);
      toast.success("Group updated successfully");
      setShowEditDialog(false);
    } catch (err) {
      console.error("Failed to update group:", err);
      toast.error("Failed to update group");
    } finally {
      setEditSubmitting(false);
    }
  };

  // =============================================
  // Permissions
  // =============================================

  const canEdit = can("edit", "groups") || isAdmin;
  // LMS: "which group reviews THIS team's assessments" — a grant on
  // lms/team/<this group's id>. Shown only where the org has LMS at all.
  const canManageReviewers = canEdit && can("view", "lms");

  // =============================================
  // Formatting
  // =============================================

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // =============================================
  // Render
  // =============================================

  if (!organisationId) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              Please select an organisation.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loadingGroup) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (groupError || !group) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-destructive">
              {groupError || "Group not found"}
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate({ to: "/portal/platform/groups" })}
            >
              Back to Groups
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Link
              to="/portal/platform/groups"
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Groups
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <UsersRound className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                {group.name}
                <Badge
                  variant={group.is_active ? "success" : "destructive"}
                >
                  {group.is_active ? (
                    <span className="flex items-center">
                      <CheckCircle className="h-3 w-3 mr-1" /> Active
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <XCircle className="h-3 w-3 mr-1" /> Inactive
                    </span>
                  )}
                </Badge>
              </h1>
              {group.description && (
                <p className="text-sm text-muted-foreground">
                  {group.description}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canManageReviewers && (
            <Button
              variant="outline"
              onClick={() => setShowReviewersDialog(true)}
              data-testid="lms-assessment-reviewers"
            >
              <ClipboardCheck className="h-4 w-4 mr-2" />
              Assessment reviewers
            </Button>
          )}
          {canEdit && (
            <Button variant="outline" onClick={() => setShowEditDialog(true)}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit Group
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Members</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{group.member_count ?? 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="members" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Members
          </TabsTrigger>
          <TabsTrigger value="object-permissions" className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            Object Permissions
          </TabsTrigger>
        </TabsList>

        {/* Members Tab */}
        <TabsContent value="members">
          <Card>
            <CardHeader className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Group Members</CardTitle>
                  <CardDescription className="text-xs">
                    Users who belong to this group
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <SearchInput
                    value={memberSearchTerm}
                    onChange={(e) => setMemberSearchTerm(e.target.value)}
                    onClear={() => setMemberSearchTerm("")}
                    placeholder="Search members..."
                    containerClassName="w-64"
                  />
                  {canEdit && (
                    <Button
                      size="sm"
                      onClick={() => setShowAddMemberDialog(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Member
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {membersLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : members.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium mb-2">
                    {memberSearchTerm
                      ? "No members match your search"
                      : "No members yet"}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {memberSearchTerm
                      ? "Try a different search term."
                      : "Add members to grant them the object permissions in this group."}
                  </p>
                  {!memberSearchTerm && canEdit && (
                    <Button onClick={() => setShowAddMemberDialog(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Member
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <SortableTableHead
                          direction={getMemberSortDirection("email")}
                          onSort={() => handleMemberSort("email")}
                        >
                          User
                        </SortableTableHead>
                        <SortableTableHead
                          direction={getMemberSortDirection("added_at")}
                          onSort={() => handleMemberSort("added_at")}
                        >
                          Added
                        </SortableTableHead>
                        <SortableTableHead
                          direction={null}
                          onSort={() => {}}
                          disabled
                          className="w-[50px]"
                        >
                          {" "}
                        </SortableTableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {members.map((member) => (
                        <TableRow key={member.user_id || member.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
                                <Users className="h-5 w-5 text-primary" />
                              </div>
                              <div>
                                <div className="font-medium">
                                  {member.first_name && member.last_name
                                    ? `${member.first_name} ${member.last_name}`
                                    : member.username}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {member.email}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {formatDate(member.added_at)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canEdit && (
                                  <DropdownMenuItem
                                    onClick={() => setRemovingMember(member)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Remove from Group
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {membersPagination.totalCount > 0 && (
                    <div className="mt-4 border-t pt-4">
                      <Pagination
                        page={membersPagination.page}
                        totalPages={membersPagination.totalPages}
                        totalCount={membersPagination.totalCount}
                        pageSize={membersPagination.pageSize}
                        pageSizeOptions={membersPageSizeOptions}
                        startIndex={membersStartIndex}
                        endIndex={membersEndIndex}
                        canGoPrev={membersCanGoPrev}
                        canGoNext={membersCanGoNext}
                        onPageChange={membersGoToPage}
                        onPageSizeChange={membersSetPageSize}
                        onFirstPage={membersFirstPage}
                        onLastPage={membersLastPage}
                        onPrevPage={membersPrevPage}
                        onNextPage={membersNextPage}
                      />
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Object Permissions Tab */}
        <TabsContent value="object-permissions">
          <Card>
            <CardHeader className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Object Permissions</CardTitle>
                  <CardDescription className="text-xs">
                    Read-only rollup of the objects this group can access. New access is
                    granted from each object&apos;s own page — use a row&apos;s open icon to go
                    there. Existing rows can be re-roled or revoked here.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={objPermServiceFilter}
                    onValueChange={(val) => {
                      setObjPermServiceFilter(val === "all" ? "" : val);
                      setObjPermObjectTypeFilter("");
                    }}
                  >
                    <SelectTrigger className="w-40 h-9">
                      <SelectValue placeholder="All services" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All services</SelectItem>
                      {Object.entries(serviceDisplayNames).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {objPermServiceFilter && (
                    <Select
                      value={objPermObjectTypeFilter}
                      onValueChange={(val) => setObjPermObjectTypeFilter(val === "all" ? "" : val)}
                    >
                      <SelectTrigger className="w-36 h-9">
                        <SelectValue placeholder="All types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All types</SelectItem>
                        {(objectTypesByService[objPermServiceFilter] || []).map((t) => (
                          <SelectItem key={t} value={t}>
                            {t.replace(/_/g, " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {objPermsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : objectPermissions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ShieldCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium mb-2">
                    No object permissions
                  </h3>
                  <p className="text-muted-foreground">
                    {objPermServiceFilter
                      ? "No permissions match the selected filters."
                      : "This group has no object-level permissions assigned yet."}
                  </p>
                  <p className="text-muted-foreground mt-2 text-sm">
                    Grant access from the object&apos;s page — for example KB SQL → Data
                    Sources → ⋯ → Group Access.
                  </p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[44px]">
                          <span className="sr-only">Manage at object</span>
                        </TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Object Type</TableHead>
                        <TableHead>Object</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Added</TableHead>
                        {canEdit && (
                          <SortableTableHead
                            direction={null}
                            onSort={() => {}}
                            disabled
                            className="w-[50px]"
                          >
                            {" "}
                          </SortableTableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {objectPermissions.map((perm) => (
                        <TableRow key={perm.id}>
                          <TableCell>
                            <ManageAtObjectLink perm={perm} />
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {serviceDisplayNames[perm.service_name] || perm.service_name}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {perm.object_type.replace(/_/g, " ")}
                          </TableCell>
                          <TableCell>
                            <div>
                              {perm.object_name && (
                                <div className="font-medium text-sm">{perm.object_name}</div>
                              )}
                              <div className="text-xs text-muted-foreground font-mono">
                                {perm.object_id === "*" ? "All (wildcard)" : perm.object_id}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={perm.role === "owner" ? "default" : "secondary"}>
                              {perm.role}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {formatDate(perm.created_at)}
                            </span>
                          </TableCell>
                          {canEdit && (
                            <TableCell>
                              <ObjectPermissionActionsMenu
                                currentRole={perm.role}
                                availableRoles={rolesByService[perm.service_name] ?? []}
                                isUpdating={changingRolePermId === perm.id}
                                onChangeRole={(newRole) => handleChangeObjPermRole(perm, newRole)}
                                onRemove={() => setRemovingObjPerm(perm)}
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {objPermsPagination.totalCount > 0 && (
                    <div className="mt-4 border-t pt-4">
                      <Pagination
                        page={objPermsPagination.page}
                        totalPages={objPermsPagination.totalPages}
                        totalCount={objPermsPagination.totalCount}
                        pageSize={objPermsPagination.pageSize}
                        pageSizeOptions={objPermsPageSizeOptions}
                        startIndex={objPermsStartIndex}
                        endIndex={objPermsEndIndex}
                        canGoPrev={objPermsCanGoPrev}
                        canGoNext={objPermsCanGoNext}
                        onPageChange={objPermsGoToPage}
                        onPageSizeChange={objPermsSetPageSize}
                        onFirstPage={objPermsFirstPage}
                        onLastPage={objPermsLastPage}
                        onPrevPage={objPermsPrevPage}
                        onNextPage={objPermsNextPage}
                      />
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Add Member Dialog */}
      <Dialog
        open={showAddMemberDialog}
        onOpenChange={(open) => {
          setShowAddMemberDialog(open);
          if (!open) {
            setSelectedUserIds(new Set());
            setAddMemberSearchTerm("");
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Add Members to Group</DialogTitle>
            <DialogDescription>
              Select users to add to "{group.name}". They will gain all object
              permissions assigned to this group.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <SearchInput
              value={addMemberSearchTerm}
              onChange={(e) => setAddMemberSearchTerm(e.target.value)}
              onClear={() => setAddMemberSearchTerm("")}
              placeholder="Search users by name or email..."
              containerClassName="w-full"
            />
          </div>
          <div className="flex-1 overflow-y-auto py-2">
            {availableUsersLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : availableUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>
                  {addMemberSearchTerm
                    ? "No users match your search"
                    : "All users are already members of this group."}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {availableUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer"
                    onClick={() => handleToggleUser(user.id)}
                  >
                    <Checkbox
                      checked={selectedUserIds.has(user.id)}
                      onClick={(e) => e.stopPropagation()}
                      onCheckedChange={() => handleToggleUser(user.id)}
                    />
                    <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center">
                      <Users className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">
                        {user.first_name && user.last_name
                          ? `${user.first_name} ${user.last_name}`
                          : user.email}
                      </div>
                      {user.first_name && user.last_name && (
                        <div className="text-sm text-muted-foreground">
                          {user.email}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {availableUsersPagination.totalCount > 0 && (
            <div className="border-t pt-2">
              <Pagination
                page={availableUsersPagination.page}
                totalPages={availableUsersPagination.totalPages}
                totalCount={availableUsersPagination.totalCount}
                pageSize={availableUsersPagination.pageSize}
                pageSizeOptions={[10, 25, 50]}
                startIndex={availableUsersStartIndex}
                endIndex={availableUsersEndIndex}
                canGoPrev={availableUsersCanGoPrev}
                canGoNext={availableUsersCanGoNext}
                onPageChange={availableUsersGoToPage}
                onPageSizeChange={availableUsersSetPageSize}
                onFirstPage={availableUsersFirstPage}
                onLastPage={availableUsersLastPage}
                onPrevPage={availableUsersPrevPage}
                onNextPage={availableUsersNextPage}
              />
            </div>
          )}
          <DialogFooter className="border-t pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowAddMemberDialog(false);
                setSelectedUserIds(new Set());
                setAddMemberSearchTerm("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddMembers}
              disabled={selectedUserIds.size === 0 || addingMember}
            >
              {addingMember && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add {selectedUserIds.size > 0
                ? `${selectedUserIds.size} Member(s)`
                : "Members"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Member Dialog */}
      <AlertDialog
        open={!!removingMember}
        onOpenChange={(open) => !open && setRemovingMember(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Member from Group?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove "{removingMember?.email}" from "
              {group.name}"? They will lose all object permissions granted
              through this group.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removingMemberSubmitting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              disabled={removingMemberSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removingMemberSubmitting && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Remove Member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Object Permission Dialog */}
      <AlertDialog
        open={!!removingObjPerm}
        onOpenChange={(open) => !open && setRemovingObjPerm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Object Permission?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove the "{removingObjPerm?.role}" permission
              on "{removingObjPerm?.object_name || removingObjPerm?.object_id}" from "{group.name}"?
              Group members will lose access to this object.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removingObjPermSubmitting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveObjPerm}
              disabled={removingObjPermSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removingObjPermSubmitting && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Remove Permission
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* LMS assessment reviewers for this team. The group(s) granted here at
          Editor or above review this team's test attempts (dp lms enforces
          it); Viewer grants nothing reviewable, hence the Editor default. */}
      {canManageReviewers && (
        <ObjectGroupAccessDialog
          serviceName="lms"
          objectType="team"
          objectId={group.id}
          objectName={group.name}
          defaultRole="editor"
          open={showReviewersDialog}
          onOpenChange={setShowReviewersDialog}
        />
      )}

      {/* Edit Group Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Group</DialogTitle>
            <DialogDescription>
              Update the group details below.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-name">Group Name</Label>
                <Input
                  id="edit-name"
                  value={editFormData.name}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, name: e.target.value })
                  }
                  placeholder="e.g., Full Access, Read Only, Reporting Team"
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="edit-description">Description (optional)</Label>
                <Textarea
                  id="edit-description"
                  value={editFormData.description}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Describe what this group is for..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditDialog(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={editSubmitting}>
                {editSubmitting && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}
